/**
 * Привязка приёма лекарства к режиму питания.
 *
 * Врач формулирует назначение через еду («за 30 минут до еды»), а напоминание
 * умеет только время. Здесь этот перевод и живёт: расписание питания из режима
 * дня превращается в конкретные минуты приёма и обратно — в подпись «Завтрак,
 * за 30 мин до еды» рядом с дозой.
 *
 * Функции чистые и не знают про Dexie: на входе — список слотов питания,
 * на выходе — минуты от полуночи.
 */

import { clampMinutes, MINUTES_IN_HOUR, type MinutesOfDay } from '@/shared/lib/datetime';
import type { MealRelation } from '@/shared/db/schema';

/** Минимально необходимое описание приёма пищи из режима дня. */
export interface MealSlot {
  /** Правило режима дня: по нему курс и запоминает привязку. */
  routineId: string;
  title: string;
  startMin: MinutesOfDay;
  durationMin: number;
}

/** Насколько раньше еды принимают препарат натощак, если явного смещения нет. */
const EMPTY_STOMACH_LEAD_MIN = 30;

/**
 * За сколько до сна принимают препарат «перед сном».
 *
 * Полчаса — не медицинское правило, а бытовое: таблетку принимают, уже
 * собираясь ложиться, а не в момент, когда голова касается подушки. Ставить
 * приём ровно на начало сна значило бы напоминать о нём человеку, который уже
 * уснул.
 */
export const BEDTIME_LEAD_MIN = 30;

/**
 * Время приёма «перед сном» — от начала сна по режиму дня.
 *
 * Раньше для этой связи расписание подобрать было нельзя: кнопка подстановки
 * считала время только от еды и на «перед сном» всегда отвечала отказом. Но
 * начало сна известно ровно так же, как время завтрака, — считать от него ничем
 * не сложнее.
 */
export function doseTimeBeforeSleep(sleepStartMin: MinutesOfDay): MinutesOfDay {
  return clampMinutes(sleepStartMin - BEDTIME_LEAD_MIN);
}

/** Время приёма относительно конкретного приёма пищи. */
export function doseTimeForMeal(
  meal: MealSlot,
  relation: MealRelation,
  offsetMin: number,
): MinutesOfDay {
  const offset = Math.max(0, Math.round(offsetMin));

  switch (relation) {
    case 'beforeMeal':
      return clampMinutes(meal.startMin - offset);
    case 'emptyStomach':
      return clampMinutes(meal.startMin - (offset > 0 ? offset : EMPTY_STOMACH_LEAD_MIN));
    case 'afterMeal':
      // Отсчёт от окончания еды: «через час после обеда» — это час после того,
      // как человек поел, а не после того, как сел за стол.
      return clampMinutes(meal.startMin + meal.durationMin + offset);
    case 'withMeal':
      return clampMinutes(meal.startMin);
    case 'any':
    case 'bedtime':
      return clampMinutes(meal.startMin);
  }
}

/** Связь считается «к еде», если время приёма считается от приёма пищи. */
export function isMealBound(relation: MealRelation): boolean {
  return relation !== 'any' && relation !== 'bedtime';
}

/**
 * Времена приёма для выбранных приёмов пищи.
 *
 * Раньше выбора не было: подстановка сама разносила приёмы «равномерно по
 * дню» — при двух дозах это оказывались завтрак и ужин, и человеку,
 * принимающему лекарство после завтрака и после обеда, приходилось править
 * время руками. Какие именно приёмы пищи нужны, знает только он.
 */
export function timesForMeals(
  meals: readonly MealSlot[],
  relation: MealRelation,
  offsetMin: number,
): MinutesOfDay[] {
  if (!isMealBound(relation)) return [];

  const times = meals.map((meal) => doseTimeForMeal(meal, relation, offsetMin));
  return [...new Set(times)].sort((a, b) => a - b);
}

/**
 * Приёмы пищи, к которым привязаны текущие времена.
 *
 * Выбор не хранится отдельным полем: он выводится из самих времён. Так в базе
 * остаётся один источник истины — расписание приёмов, — а форма при открытии
 * показывает то, что там на самом деле, а не то, что когда-то выбрали.
 * Сравнение точное: приблизительное совпадение молча «притягивало» бы к еде
 * время, выставленное вручную.
 */
export function mealsForTimes(
  meals: readonly MealSlot[],
  relation: MealRelation,
  offsetMin: number,
  doseTimes: readonly MinutesOfDay[],
): Set<string> {
  if (!isMealBound(relation)) return new Set();

  const chosen = new Set<string>();
  for (const meal of meals) {
    if (doseTimes.includes(doseTimeForMeal(meal, relation, offsetMin))) chosen.add(meal.title);
  }
  return chosen;
}

/** Максимальное расхождение, при котором доза ещё считается привязанной к еде. */
const MATCH_TOLERANCE_MIN = 90;

/**
 * Приём пищи, к которому относится доза.
 * Нужен, чтобы в ленте дня рядом со временем стояло «Завтрак · за 30 мин до еды».
 */
export function findRelatedMeal(
  meals: MealSlot[],
  scheduledMin: MinutesOfDay,
  relation: MealRelation,
  offsetMin: number,
  toleranceMin: number = MATCH_TOLERANCE_MIN,
): MealSlot | null {
  if (relation === 'any' || relation === 'bedtime') return null;

  let best: MealSlot | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const meal of meals) {
    const expected = doseTimeForMeal(meal, relation, offsetMin);
    const distance = Math.abs(expected - scheduledMin);
    if (distance < bestDistance) {
      best = meal;
      bestDistance = distance;
    }
  }

  return bestDistance <= toleranceMin ? best : null;
}

/** Приёмы пищи в пределах ближайших суток от времени — для подсказки в форме. */
export function nextMealAfter(meals: MealSlot[], minutes: MinutesOfDay): MealSlot | null {
  const sorted = [...meals].sort((a, b) => a.startMin - b.startMin);
  return sorted.find((meal) => meal.startMin >= minutes) ?? sorted[0] ?? null;
}

/** Час в минутах — вспомогательная константа для форм ввода смещения. */
export const OFFSET_PRESETS: MinutesOfDay[] = [0, 15, 30, 60, 2 * MINUTES_IN_HOUR];
